/**
 * Core FutureX domain types shared between the CLI and the backend.
 */

/** Role of a single conversation turn. */
export type Role = 'system' | 'user' | 'assistant';

/** One message in a conversation. */
export interface Message {
  readonly role: Role;
  readonly content: string;
}

/**
 * Thinking control. `false` forces a fast answer; `true` enables thinking at the
 * provider default effort; `{ effort }` selects the level. DeepSeek accepts only
 * `high | max` (see `@fim/ai-sdk`), never the OpenAI-style low/medium/high.
 */
export type ReasoningOption = boolean | { readonly effort?: 'high' | 'max' };

/**
 * The three real DeepSeek effort states (Blueprint v2 §3.2), named for the CLI +
 * tier layer:
 *  - `off`  — thinking disabled (fast, non-thinking).
 *  - `high` — thinking enabled, reasoning_effort=high.
 *  - `max`  — thinking enabled, reasoning_effort=max (a.k.a. xhigh).
 * DeepSeek has no low/medium rung — the cheaper lever is `off` or a smaller model.
 */
export type EffortLevel = 'off' | 'high' | 'max';

/**
 * FutureX product tiers (Blueprint v2 §3.3) — each maps to a model + default
 * effort + an allowed-override set. The routing table + enforcing resolver live in
 * `tier.ts` ({@link TIERS}, {@link resolveTier}).
 */
export type Tier = 'fx-pro' | 'fx-fast' | 'fx-mini' | 'fx-eco';

/** Translate a named {@link EffortLevel} into the SDK's {@link ReasoningOption}. */
export function effortToReasoning(effort: EffortLevel): ReasoningOption {
  return effort === 'off' ? false : { effort };
}

/** Classify a {@link ReasoningOption} back into a named {@link EffortLevel}. */
export function reasoningToEffort(reasoning: ReasoningOption | undefined): EffortLevel {
  if (!reasoning) return 'off'; // undefined or false → thinking disabled
  if (reasoning === true) return 'high'; // provider default effort is high
  return reasoning.effort ?? 'high';
}

/**
 * A request for one assistant turn. The CLI builds this; the {@link FutureXClient}
 * turns it into a model (or gateway) call.
 */
export interface ChatTurn {
  /**
   * The stable, cache-targeted prefix (system prompt + project memory + repo
   * card). Pinned for the life of a session so DeepSeek prefix-caching hits.
   */
  readonly system?: string;
  /** Conversation history, with the new user message last. At least one entry. */
  readonly messages: readonly Message[];
  /** Thinking control. Defaults to off (fast). */
  readonly reasoning?: ReasoningOption;
  /** Override the model id (e.g. `deepseek-v4-pro`). */
  readonly model?: string;
  /** Upper bound on output tokens. */
  readonly maxTokens?: number;
  /**
   * The {@link Tier} this turn routed through, when one was selected. The gateway
   * re-resolves and ENFORCES it server-side (overriding model/reasoning/maxTokens),
   * so it is sent alongside the already-resolved values. Absent for an untiered turn.
   */
  readonly tier?: Tier;
  /** The effort level chosen for {@link tier} (resolved default or override). */
  readonly effort?: EffortLevel;
  /**
   * True only for the user's FIRST round of a turn — the round whose last message
   * is the human's actual prompt. The agent loop's continuation rounds (length
   * continues, finish nudges, verify-repair) replay synthetic `user` messages and
   * MUST leave this unset/false. The gateway uses it to log exactly the real prompt
   * to the audit log and ignore loop scaffolding. Absent ⇒ not a fresh user turn.
   */
  readonly newTurn?: boolean;
  /** Abort signal to cancel the in-flight turn (e.g. Ctrl+C). */
  readonly signal?: AbortSignal;
}

/**
 * A tool the CLI can execute, described for the model. The gateway feeds these
 * descriptors to DeepSeek to drive the agent loop; the executable implementation
 * (and Zod validation of the model's arguments) lives CLIENT-SIDE in
 * `@fim/futurex-tools`. The CLI advertises its descriptors at session open
 * ({@link OpenSessionRequest.tools}), so the backend never needs to depend on the
 * tool package and a client can expose a platform-specific tool set.
 */
export interface ToolDescriptor {
  readonly name: string;
  readonly description: string;
  /** JSON Schema for the tool's arguments — exactly what the model sees. */
  readonly parameters: Record<string, unknown>;
  /** Mutating tools are permission-gated on the CLI before they run. */
  readonly mutating: boolean;
}

/**
 * A compact summary of the working repository, folded into the stable prefix so
 * the assistant is grounded in the project's stack without dumping files.
 */
export interface RepoCard {
  readonly root: string;
  readonly name?: string;
  readonly language?: string;
  readonly framework?: string;
  readonly backend?: string;
  readonly orm?: string;
  readonly packageManager?: string;
  readonly test?: string;
}
